import { Injectable } from '@angular/core';
import { FormArray, FormControl, FormGroup, FormRecord } from '@angular/forms';
import { AssetResource } from '@models/Core/Asset';
import {
  AudioAsset,
  AudioClip,
  AudioClipData,
  AudioProject,
  AudioProjectData,
  AudioSegment,
  AudioSegmentData,
} from '@models/Sessions/Idea/AudioProject';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import isObject from 'lodash/isObject';
import uniqueId from 'lodash/uniqueId';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  startWith,
} from 'rxjs';

export interface AudioClipFormControl {
  id: FormControl<string>;
  asset: FormControl<AudioAsset>;
}

export type AudioClipFormValue = FormGroup<AudioClipFormControl>['value'];

export interface AudioSegmentFormControl {
  id?: FormControl<string>;
  text: FormControl<string>;
  selectedClipIndex: FormControl<number>;
  clips: FormArray<FormGroup<AudioClipFormControl>>;
  $key: FormControl<string>;
}

export type AudioSegmentFormValue = FormGroup<AudioSegmentFormControl>['value'];

export interface AudioProjectFormControl {
  segments: FormArray<FormGroup<AudioSegmentFormControl>>;
  assets: FormRecord<FormControl<AudioAsset>>;
}

export type AudioProjectFormValue = FormGroup<AudioProjectFormControl>['value'];

export const createClipControl = (clip: AudioClip): AudioClipFormControl => {
  return {
    id: new FormControl(clip._id, {
      nonNullable: true,
    }),
    asset: new FormControl<AudioAsset>(clip.asset),
  };
};

export const reCreateClipControl = (
  value: AudioClipFormValue,
): AudioClipFormControl => {
  return {
    id: new FormControl(value.id, {
      nonNullable: true,
    }),
    asset: new FormControl<AudioAsset>(value.asset),
  };
};

interface HasClipChangesParams {
  previous?: AudioClipFormValue;
  current?: AudioClipFormValue;
}

export const hasClipChanges = ({
  previous,
  current,
}: HasClipChangesParams): boolean => {
  return (
    !isEqual(isObject(previous), isObject(current)) ||
    !isEqual(previous.id, current.id)
  );
};

interface HasClipsChangesParams {
  previous?: AudioClipFormValue[];
  current?: AudioClipFormValue[];
}

export const hasClipsChanges = ({
  previous,
  current,
}: HasClipsChangesParams): boolean => {
  return (
    previous.length !== current.length ||
    previous.some((previous, index) =>
      hasClipChanges({ previous, current: current.at(index) }),
    )
  );
};

export const mapClipFormValueToData = (
  clip: AudioClipFormValue,
): AudioClipData => {
  return {
    id: clip.id,
  };
};

export const createSegmentControl = (
  segment: AudioSegment,
): AudioSegmentFormControl => {
  return {
    id: new FormControl(segment._id, {
      nonNullable: true,
    }),
    text: new FormControl(segment.text, {
      nonNullable: true,
    }),
    selectedClipIndex: new FormControl(segment.selectedClipIndex, {
      nonNullable: true,
    }),
    clips: new FormArray<FormGroup<AudioClipFormControl>>(
      segment.clips.map(
        clip => new FormGroup<AudioClipFormControl>(createClipControl(clip)),
      ),
    ),
    $key: new FormControl(
      {
        value: uniqueId('segment'),
        disabled: true,
      },
      {
        nonNullable: true,
      },
    ),
  };
};

export const createEmptySegmentControl = (): AudioSegmentFormControl => {
  return {
    text: new FormControl(''),
    selectedClipIndex: new FormControl(-1),
    clips: new FormArray<FormGroup<AudioClipFormControl>>([]),
    $key: new FormControl(
      {
        value: uniqueId('segment'),
        disabled: true,
      },
      {
        nonNullable: true,
      },
    ),
  };
};

export const reCreateSegmentControl = (
  value: AudioSegmentFormValue,
): AudioSegmentFormControl => {
  return {
    id: new FormControl(value.id, {
      nonNullable: true,
    }),
    text: new FormControl(value.text, {
      nonNullable: true,
    }),
    selectedClipIndex: new FormControl(value.selectedClipIndex, {
      nonNullable: true,
    }),
    clips: new FormArray<FormGroup<AudioClipFormControl>>(
      value.clips.map(
        value =>
          new FormGroup<AudioClipFormControl>(reCreateClipControl(value)),
      ),
    ),
    $key: new FormControl(
      {
        value: uniqueId('segment'),
        disabled: true,
      },
      {
        nonNullable: true,
      },
    ),
  };
};

interface HasSegmentChangesParams {
  previous?: AudioSegmentFormValue;
  current?: AudioSegmentFormValue;
}

export const hasSegmentChanges = ({
  previous,
  current,
}: HasSegmentChangesParams): boolean => {
  return (
    !isEqual(isObject(previous), isObject(current)) ||
    !isEqual(previous.id, current.id) ||
    !isEqual(previous.text, current.text) ||
    !isEqual(previous.selectedClipIndex, current.selectedClipIndex) ||
    hasClipsChanges({ previous: previous.clips, current: current.clips })
  );
};

interface HasSegmentsChangesParams {
  previous?: AudioSegmentFormValue[];
  current?: AudioSegmentFormValue[];
}

export const hasSegmentsChanges = ({
  previous,
  current,
}: HasSegmentsChangesParams): boolean => {
  return (
    previous.length !== current.length ||
    previous.some((previous, index) =>
      hasSegmentChanges({ previous, current: current.at(index) }),
    )
  );
};

export const mapSegmentFormValueToData = (
  segment: AudioSegmentFormValue,
): AudioSegmentData => {
  return {
    id: segment.id,
    text: segment.text,
    selectedClipIndex: segment.selectedClipIndex,
    clips: segment.clips.map(mapClipFormValueToData),
  };
};

export const createAssetsControl = (
  assets: Record<string, AudioAsset>,
): FormRecord<FormControl<AudioAsset>> => {
  return Object.entries(assets).reduce((acc, [key, asset]) => {
    acc.addControl(key, new FormControl<AudioAsset>(asset));
    return acc;
  }, new FormRecord<FormControl<AudioAsset>>({}));
};

export const createControl = (
  audioProject: AudioProject,
): AudioProjectFormControl => {
  return {
    segments: new FormArray<FormGroup<AudioSegmentFormControl>>(
      audioProject.segments.map(
        segment =>
          new FormGroup<AudioSegmentFormControl>(createSegmentControl(segment)),
      ),
    ),
    assets: createAssetsControl(audioProject.assets),
  };
};

export const reCreateControl = (
  audioProject: AudioProjectFormValue,
): AudioProjectFormControl => {
  return {
    segments: new FormArray<FormGroup<AudioSegmentFormControl>>(
      audioProject.segments.map(
        segment =>
          new FormGroup<AudioSegmentFormControl>(
            reCreateSegmentControl(segment),
          ),
      ),
    ),
    assets: createAssetsControl(audioProject.assets),
  };
};

interface HasChangesParams {
  previous?: AudioProjectFormValue;
  current?: AudioProjectFormValue;
}

export const hasChanges = ({
  previous,
  current,
}: HasChangesParams): boolean => {
  return hasSegmentsChanges({
    previous: previous.segments,
    current: current.segments,
  });
};

export const mapFormValueToData = (
  audioProject: AudioProjectFormValue,
): AudioProjectData => {
  return {
    segments: audioProject.segments.map(mapSegmentFormValueToData),
  };
};

@Injectable()
export class AudioProjectFormService {
  private activeResource = new BehaviorSubject<AssetResource | null>(null);
  private initialValue = new BehaviorSubject<AudioProjectFormValue | undefined>(
    undefined,
  );

  setActiveResource(resource: AssetResource) {
    this.activeResource.next(resource);
  }

  $onActiveResourceChange(resource: AssetResource) {
    return this.activeResource.pipe(
      filter(Boolean),
      filter(({ path }) => path !== resource.path),
    );
  }

  $onChanges(
    form: FormGroup<AudioProjectFormControl>,
  ): Observable<[boolean, AudioProjectFormValue | undefined]> {
    const initialValue$ = this.$getInitialValue();
    const valueChanges$ = form.valueChanges.pipe(startWith(form.value));

    return combineLatest([initialValue$, valueChanges$]).pipe(
      map(([previous, current]) => {
        return [
          previous ? hasChanges({ previous, current }) : false,
          current,
        ] satisfies [boolean, AudioProjectFormValue | undefined];
      }),
      distinctUntilChanged(),
    );
  }

  setInitialValue(initialValue: AudioProjectFormValue): this {
    this.initialValue.next(initialValue);
    return this;
  }

  getInitialValue() {
    return this.initialValue.getValue();
  }

  private $getInitialValue() {
    return this.initialValue.pipe(distinctUntilChanged());
  }

  setSegmentInitialValue(segment: FormGroup<AudioSegmentFormControl>): this {
    const _initialValue = cloneDeep(this.initialValue.getValue());
    if (!_initialValue) {
      return this;
    }
    _initialValue.segments.forEach((_segment, index, segments) => {
      if (isEqual(_segment.id, segment.value.id)) {
        segments[index] = segment.value;
      }
    });
    this.initialValue.next(_initialValue);
    return this;
  }

  $onSegmentChanges(
    segment: FormGroup<AudioSegmentFormControl>,
  ): Observable<[boolean, AudioSegmentFormValue | undefined]> {
    const segmentInitialValue$ = this.$getSegmentInitialValue(segment);
    const valueChanges$ = segment.valueChanges.pipe(startWith(segment.value));

    return combineLatest([segmentInitialValue$, valueChanges$]).pipe(
      map(([previous, current]) => {
        return [
          previous ? hasSegmentChanges({ previous, current }) : false,
          current,
        ] satisfies [boolean, AudioSegmentFormValue | undefined];
      }),
    );
  }

  getSegmentInitialValue(
    segment: FormGroup<AudioSegmentFormControl>,
  ): AudioSegmentFormValue | undefined {
    const initialValue = this.getInitialValue();
    return this.getSegmentValue(segment)(initialValue);
  }

  private getSegmentValue(segment: FormGroup<AudioSegmentFormControl>) {
    return (
      value?: AudioProjectFormValue,
    ): AudioSegmentFormValue | undefined => {
      return value?.segments?.find?.(({ id }) => {
        return isEqual(id, segment.value.id);
      });
    };
  }

  private $getSegmentInitialValue(segment: FormGroup<AudioSegmentFormControl>) {
    return this.initialValue.pipe(
      map(value => {
        return this.getSegmentValue(segment)(value);
      }),
      distinctUntilChanged(),
    );
  }

  private __segment__(clip: FormGroup<AudioClipFormControl>) {
    return clip.parent.parent as FormGroup<AudioSegmentFormControl>;
  }

  setClipInitialValue(clip: FormGroup<AudioClipFormControl>): this {
    const _initialValue = cloneDeep(this.initialValue.getValue());
    if (!_initialValue) {
      return this;
    }
    _initialValue.segments.forEach(_segment => {
      _segment.clips.forEach((_clip, index) => {
        if (isEqual(_clip.id, clip.value.id)) {
          _segment.clips[index] = clip.value;
        }
      });
    });
    this.initialValue.next(_initialValue);
    return this;
  }

  $onClipChanges(
    clip: FormGroup<AudioClipFormControl>,
  ): Observable<[boolean, AudioClipFormValue | undefined]> {
    const clipInitialValue$ = this.$getClipInitialValue(clip);
    const valueChanges$ = clip.valueChanges.pipe(startWith(clip.value));

    return combineLatest([clipInitialValue$, valueChanges$]).pipe(
      map(([previous, current]) => {
        return [
          previous ? hasClipChanges({ previous, current }) : false,
          current,
        ] satisfies [boolean, AudioClipFormValue | undefined];
      }),
    );
  }

  getClipInitialValue(
    clip: FormGroup<AudioClipFormControl>,
  ): AudioClipFormValue | undefined {
    const initialValue = this.getSegmentInitialValue(this.__segment__(clip));
    return this.getClipValue(clip)(initialValue);
  }

  private getClipValue(clip: FormGroup<AudioClipFormControl>) {
    return (
      segment?: AudioSegmentFormValue,
    ): AudioClipFormValue | undefined => {
      return segment?.clips?.find?.(({ id }) => {
        return isEqual(id, clip.value.id);
      });
    };
  }

  private $getClipInitialValue(clip: FormGroup<AudioClipFormControl>) {
    const segment = this.__segment__(clip);

    return this.initialValue.pipe(
      map(value => {
        return this.getSegmentValue(segment)(value);
      }),
      distinctUntilChanged(),
    );
  }

  setAssetInitialValue(asset: FormControl<AudioAsset>) {
    const _initialValue = cloneDeep(this.initialValue.getValue());
    if (!_initialValue) {
      return this;
    }
    if (_initialValue.assets[asset.value._id]) {
      _initialValue.assets[asset.value._id] = asset.value;
    }
    this.initialValue.next(_initialValue);
    return this;
  }
}
